import keycloak from './keycloak';
import type Keycloak from 'keycloak-js';
import { invalidateItemListCache } from '../utils/itemListCache';

declare global {
  interface Window {
    __API_URL?: string;
  }
}

// Get the API URL from environment variables
console.log('🌐 Environment check:', {
  hostname: window.location.hostname,
  protocol: window.location.protocol,
  port: window.location.port
});

const getApiUrl = (): string => {
  // Check if we have a configured API URL from the build
  if (typeof window !== 'undefined' && window.__API_URL) {
    return `https://${window.__API_URL}/api/v1`;
  }

  // Fallback based on current hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'app.finderz.ai' || hostname.includes('app.finderz.ai')) {
      return 'https://api.finderz.ai/api/v1';
    }
    else if (hostname === 'preprod.finderz.ai' || hostname.includes('preprod.finderz.ai')) {
      return 'https://api.preprod.finderz.ai/api/v1';
    }
    else if (hostname === 'app.stage.finderz.dev' || hostname.includes('stage.finderz.dev')) {
      return 'https://api.stage.finderz.dev/api/v1';
    } else if (hostname === 'app.finderz.dev' || hostname.includes('finderz.dev')) {
      return 'https://api.finderz.dev/api/v1';
    } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000/api/v1';
    }
  }

  // Default fallback - use finderz.dev for production calls
  return 'https://api.finderz.dev/api/v1';
};

export const API_URL = getApiUrl();

// API Error handler for improved error handling
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public response?: unknown,
    message?: string
  ) {
    super(message || `HTTP ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

// Centralized API request handler with automatic token refresh
export const apiRequest = async (
  url: string,
  options: RequestInit = {},
  keycloak?: Keycloak
): Promise<Response> => {
  try {
    // Ensure headers exist
    options.headers = options.headers || {};

    // Make the initial request
    let response = await fetch(url, options);

    // Handle different HTTP status codes
    if (!response.ok) {
      let errorBody = '';
      try {
        errorBody = await response.text();
      } catch {
        // Could not read error response body
      }

      // Special handling for different error types
      if (response.status === 401) {
        console.error('❌ 401 Unauthorized - token may be expired');
        if (keycloak) {
          console.log('🔄 Attempting token refresh for 401...');
          try {
            // Force token refresh (0 means refresh if expired, -1 means force refresh)
            const refreshed = await keycloak.updateToken(0);
            console.log('🔄 Token refresh result:', refreshed);

            if (refreshed || keycloak.token) {
              console.log('✅ Token refreshed, retrying request...');
              console.log('🔗 New token (first 20 chars):', keycloak.token?.substring(0, 20) + '...');

              // Retry request with new token
              options.headers = {
                ...options.headers,
                'authorization': `Bearer ${keycloak.token}`
              };
              response = await fetch(url, options);

              console.log('🔄 Retry response status:', response.status, response.ok);

              // Check if retry was successful
              if (response.ok) {
                console.log('✅ Retry successful after token refresh');
                return response;
              } else {
                // Even after refresh, still getting error
                let retryErrorBody = '';
                try {
                  retryErrorBody = await response.text();
                } catch {
                  // Could not read retry error response body
                }
                console.error('❌ Retry failed even after token refresh:', response.status, retryErrorBody);
                throw new ApiError(response.status, response.statusText, retryErrorBody, 'Request failed even after token refresh');
              }
            } else {
              console.error('❌ Token refresh did not provide new token');
              keycloak.login();
              throw new ApiError(401, 'Unauthorized', errorBody, 'Token refresh failed - please log in again');
            }
          } catch (refreshError) {
            console.error('❌ Token refresh exception:', refreshError);
            // Handle specific token refresh errors
            if (refreshError instanceof Error) {
              console.error('❌ Token refresh error details:', refreshError.message);
            }

            // Don't throw unhandled rejection, handle gracefully
            try {
            keycloak.login();
            } catch (loginError) {
              console.error('❌ Login redirect failed:', loginError);
            }

            throw new ApiError(401, 'Unauthorized', errorBody, 'Session expired, please log in again');
          }
        }
        throw new ApiError(401, 'Unauthorized', errorBody, 'Authentication required');
      }

      if (response.status === 403) {
        throw new ApiError(403, 'Forbidden', errorBody, 'You do not have permission to access this resource');
      }

      if (response.status === 502) {
        throw new ApiError(502, 'Bad Gateway', errorBody, 'Server temporarily unavailable. Please try again in a few moments.');
      }

      if (response.status === 503) {
        throw new ApiError(503, 'Service Unavailable', errorBody, 'Service temporarily unavailable. Please try again later.');
      }

      if (response.status === 504) {
        throw new ApiError(504, 'Gateway Timeout', errorBody, 'Request timed out. Please try again.');
      }

      if (response.status >= 500) {
        throw new ApiError(response.status, response.statusText, errorBody, 'Server error occurred. Please try again later.');
      }

      // For other client errors (4xx)
      throw new ApiError(response.status, response.statusText, errorBody);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network errors, etc.
    console.error('Network or unexpected error:', error);
    throw new Error('Network error. Please check your connection and try again.');
  }
};

// Helper function to safely parse JSON responses
export const safeJsonParse = async <T = unknown>(response: Response): Promise<T | null> => {
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    console.warn('Failed to parse JSON response:', text);
    throw new Error('Invalid JSON response from server');
  }
};

/**
 * Extract a human readable message out of an error response body.
 * FastAPI returns `{ detail: string }` or `{ detail: [{ msg }] }` for 422s.
 */
export const parseApiErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    const detail = body?.detail;

    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (typeof body?.message === 'string') return body.message;
  } catch {
    // Body was empty or not JSON - fall through to the default message
  }

  return fallback;
};

console.log('✅ API configuration loaded:', API_URL);

// ============================================
// Authenticated Fetch - Main API utility
// ============================================

// Token refresh state (singleton)
let isRefreshingToken = false;
let refreshPromise: Promise<boolean> | null = null;
let lastTokenRefreshAttempt = 0;
const TOKEN_REFRESH_MIN_INTERVAL = 5000; // 5 seconds between refresh attempts

/**
 * Ensure token is valid before making a request.
 * Will refresh the token if it's expired or expiring soon.
 */
export const ensureValidToken = async (): Promise<string | null> => {
  if (!keycloak.authenticated) {
    console.log('🔐 Not authenticated, cannot ensure valid token');
    return null;
  }

  if (!keycloak.token) {
    console.log('🔐 No token available');
    return null;
  }

  try {
    // Check if token expires in the next 60 seconds
    const isExpiringSoon = keycloak.isTokenExpired(60);
    
    if (isExpiringSoon) {
      console.log('🔄 Token expiring soon, refreshing before request...');
      
      // Prevent multiple concurrent refreshes
      if (isRefreshingToken && refreshPromise) {
        console.log('⏳ Waiting for existing token refresh...');
        await refreshPromise;
        return keycloak.token || null;
      }

      // Rate limit refresh attempts
      const now = Date.now();
      if (now - lastTokenRefreshAttempt < TOKEN_REFRESH_MIN_INTERVAL) {
        console.log('⏳ Token refresh rate limited, using current token');
        return keycloak.token;
      }

      isRefreshingToken = true;
      lastTokenRefreshAttempt = now;

      refreshPromise = keycloak.updateToken(60)
        .then((refreshed) => {
          if (refreshed) {
            console.log('✅ Token refreshed successfully before request');
          } else {
            console.log('ℹ️ Token still valid, no refresh needed');
          }
          return true;
        })
        .catch((error) => {
          console.error('❌ Failed to refresh token:', error);
          return false;
        })
        .finally(() => {
          isRefreshingToken = false;
          refreshPromise = null;
        });

      const success = await refreshPromise;
      if (!success) {
        return null;
      }
    }

    return keycloak.token;
  } catch (error) {
    console.error('❌ Error checking token validity:', error);
    return keycloak.token || null;
  }
};

/**
 * Force refresh the token, even if it's still valid.
 * Use this after receiving a 401 error.
 */
export const forceRefreshToken = async (): Promise<string | null> => {
  if (!keycloak.authenticated) {
    console.log('🔐 Not authenticated, cannot refresh token');
    return null;
  }

  // Prevent multiple concurrent refreshes
  if (isRefreshingToken && refreshPromise) {
    console.log('⏳ Waiting for existing token refresh...');
    await refreshPromise;
    return keycloak.token || null;
  }

  // Rate limit refresh attempts
  const now = Date.now();
  if (now - lastTokenRefreshAttempt < TOKEN_REFRESH_MIN_INTERVAL) {
    console.log('⏳ Token refresh rate limited');
    return keycloak.token || null;
  }

  console.log('🔄 Force refreshing token...');
  isRefreshingToken = true;
  lastTokenRefreshAttempt = now;

  refreshPromise = keycloak.updateToken(-1) // -1 forces refresh regardless of expiry
    .then((refreshed) => {
      console.log('✅ Token force refreshed:', refreshed);
      return true;
    })
    .catch((error) => {
      console.error('❌ Force token refresh failed:', error);
      return false;
    })
    .finally(() => {
      isRefreshingToken = false;
      refreshPromise = null;
    });

  const success = await refreshPromise;
  return success ? (keycloak.token || null) : null;
};

/**
 * Main authenticated fetch function.
 * - Ensures token is valid before each request
 * - Automatically retries on 401 with fresh token
 * - Handles common error cases
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  retryCount: number = 0
): Promise<Response> => {
  const MAX_RETRIES = 1;

  // Ensure we have a valid token before the request
  const token = await ensureValidToken();
  
  if (!token) {
    console.error('❌ authenticatedFetch: No valid token available');
    // Try to redirect to login
    try {
      keycloak.login();
    } catch (e) {
      console.error('❌ Failed to redirect to login:', e);
    }
    throw new ApiError(401, 'Unauthorized', undefined, 'Authentication required. Please log in.');
  }

  // Any write to a project's items or action items (including their documents) makes that project's cached lists stale.
  if ((options.method ?? 'GET').toUpperCase() !== 'GET') {
    const itemsPath = /\/(?:projects|action_items)\/([^/?#]+)\/items/.exec(url);
    if (itemsPath) invalidateItemListCache(itemsPath[1]);
  }

  // Add authorization header
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const requestOptions: RequestInit = {
    ...options,
    headers
  };

  try {
    const response = await fetch(url, requestOptions);

    // Handle 401 errors with retry
    if (response.status === 401 && retryCount < MAX_RETRIES) {
      console.log('🔐 Received 401, attempting token refresh and retry...');
      
      const newToken = await forceRefreshToken();
      
      if (newToken) {
        console.log('🔄 Retrying request with fresh token...');
        return authenticatedFetch(url, options, retryCount + 1);
      } else {
        console.error('❌ Token refresh failed, redirecting to login...');
        try {
          keycloak.login();
        } catch (e) {
          console.error('❌ Failed to redirect to login:', e);
          window.location.reload();
        }
        throw new ApiError(401, 'Unauthorized', undefined, 'Session expired. Please log in again.');
      }
    }

    return response;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    console.error('❌ Network error in authenticatedFetch:', error);
    throw new Error('Network error. Please check your connection and try again.');
  }
};

/**
 * Get the current keycloak token (for legacy code).
 * Use authenticatedFetch instead when possible.
 */
export const getKeycloakToken = (): string | undefined => {
  return keycloak.token;
};

/**
 * Check if the user is authenticated.
 */
export const isAuthenticated = (): boolean => {
  return keycloak.authenticated || false;
};

// Visibility change and focus handlers removed.
// Token refresh on tab visibility/focus is handled by SessionNetworkManager
// to avoid race conditions with competing refresh calls.
